import { Module } from 'vuex';
import { AddBookmarkInterface, ConfigStoreState, CountdownInterface, LayoutInterface, MoveTileInterface, RemoveBookmarkInterface, TileConfig, UpdateLayoutInterface, UpdateTileInterface } from './config.types';
import { DEFAULT_CONFIG, DEFAULT_LAYOUTS } from './default-configs';

const UPDATE_LAYOUTS = "UPDATE_LAYOUTS"
const ADD_LAYOUT = "ADD_LAYOUT"
const UPDATE_LAYOUT = "UPDATE_LAYOUT"
const UPDATE_ACTIVE_LAYOUT = "UPDATE_ACTIVE_LAYOUT"
const REMOVE_LAYOUT = "REMOVE_LAYOUT"

const UPDATE_COUNTDOWNS = "UPDATE_COUNTDOWNS"

const MOVE_TILE = "MOVE_TILE";
const UPDATE_TILE = "UPDATE_TILE";
const ADD_BOOKMARK = "ADD_BOOKMARK";
const REMOVE_BOOKMARK = "REMOVE_BOOKMARK";
const UPDATE_ALL_CONFIG = "UPDATE_ALL_CONFIG";
const UPDATE_NEXT_EVENT_POPUP = "UPDATE_NEXT_EVENT_POPUP";
const UPDATE_TOUR_TAKEN = "UPDATE_TOUR_TAKEN";

export const configStore: Module<ConfigStoreState, any> = {
  namespaced: true,
  state: DEFAULT_CONFIG,
  mutations: {
    [UPDATE_LAYOUTS]: (state, payload: Array<LayoutInterface>) => {
      state.layouts = payload
    },
    [ADD_LAYOUT]: (state, payload: LayoutInterface) => {
      state.layouts.push(payload)
    },
    [UPDATE_LAYOUT]: (state, payload: UpdateLayoutInterface) => {
      state.layouts[payload.index] = {
        ...state.layouts[payload.index],
        ...payload.layout
      }
    },
    [UPDATE_ACTIVE_LAYOUT]: (state, payload: number) => {
      state.activeLayoutIndex = payload
    },
    [REMOVE_LAYOUT]: (state, payload: number) => {
      state.layouts.splice(payload, 1)
    },
    [UPDATE_COUNTDOWNS]: (state, payload: Array<CountdownInterface>) => {
      state.timers.countdowns = payload
    },
    [MOVE_TILE]: (state, payload: MoveTileInterface) => {
      let rows = state.layouts[state.activeLayoutIndex].config.length
      let rowCols = new Array<number>()
      state.layouts[state.activeLayoutIndex].config.forEach(row => rowCols.push(row.length))
      let tiles = state.layouts[state.activeLayoutIndex].config.flat()
      if (payload.dir == '-') {
        tiles.push(tiles.shift() || {} as TileConfig)
      } else if (payload.dir == '+') {
        tiles.unshift(tiles.pop() || {} as TileConfig)
      }
      
      state.layouts[state.activeLayoutIndex].config = []
      let startIndex = 0;
      for (let i = 0; i < rows; i++) {
        state.layouts[state.activeLayoutIndex].config[i] = tiles.slice(startIndex,  startIndex + rowCols[i])
        startIndex += rowCols[i]
      }
    },
    [UPDATE_TILE]: (state, payload: UpdateTileInterface) => {
      state.layouts[state.activeLayoutIndex].config[payload.index.row].splice(payload.index.col, 1, payload.tileConfig)
    },
    [ADD_BOOKMARK]: (state, payload: AddBookmarkInterface) => {
      state.bookmarks[payload.name] = payload.bookmark;
    },
    [REMOVE_BOOKMARK]: (state, payload: RemoveBookmarkInterface) => {
      delete state.bookmarks[payload.name];
    },
    [UPDATE_ALL_CONFIG]: (state, payload: ConfigStoreState) => {
      for (let k of (Object.keys(payload) as Array<keyof ConfigStoreState>)) {
        if (payload.hasOwnProperty(k) && state.hasOwnProperty(k)){
          (state as any)[k] = payload[k] as any; 
        }
      }
    },
    [UPDATE_NEXT_EVENT_POPUP]: (state, payload: boolean) => {
      state.nextEventPopup = payload
    },
    [UPDATE_TOUR_TAKEN]: (state, payload: boolean) => {
      state.tourTaken = payload
    }
  },
  actions: {
    moveTile({commit}, payload: MoveTileInterface) {
      commit(MOVE_TILE, payload)
    },
    updateTile({commit}, payload: UpdateTileInterface) {
      commit(UPDATE_TILE, payload);
    },
    addBookmark({commit}, payload: AddBookmarkInterface) {
      commit(ADD_BOOKMARK, payload);
    },
    removeBookmark({commit}, payload: RemoveBookmarkInterface) {
      commit(REMOVE_BOOKMARK, payload);
    },
    updateLayouts({commit}, payload: Array<LayoutInterface>) {
      commit(UPDATE_LAYOUTS, payload)
    },
    addLayout({commit}, payload: LayoutInterface) {
      commit(ADD_LAYOUT, payload)
    },
    updateLayout({commit}, payload: UpdateLayoutInterface) {
      commit(UPDATE_LAYOUT, payload)
    },
    updateActiveLayoutIndex({commit}, payload: number) {
      commit(UPDATE_ACTIVE_LAYOUT, payload)
    },
    removeLayout({commit}, payload: number) {
      commit(REMOVE_LAYOUT, payload)
    },
    updateCountdowns({commit}, payload: Array<CountdownInterface>) {
      let enabled = 0
      payload = payload.map((p) => {
        if (p.enabled && enabled < 3) {
          enabled++
        } else {
          p.enabled = false
        }
        return p
      })
      commit(UPDATE_COUNTDOWNS, payload)
    },
    updateAllConfig({commit}, payload: ConfigStoreState) {
      commit(UPDATE_ALL_CONFIG, payload);
    },
    resetAllConfig({commit}) {
      commit(UPDATE_ALL_CONFIG, DEFAULT_CONFIG)
    },
    updateNextEventPopup({commit}, payload: boolean) {
      commit(UPDATE_NEXT_EVENT_POPUP, payload)
    },
    updateTourTaken({commit}, payload: boolean) {
      commit(UPDATE_TOUR_TAKEN, payload)
    }
  },
  getters: {
    getBookmarkArray: (state) => () => {
      return Object.entries(state.bookmarks);
    },
    getBookmark: (state) => (name: string) => {
      return state.bookmarks[name];
    },
    getLayout: (state) => (idx?: number) => {
      if (typeof idx == "number") {
        return state.layouts[idx]
      } else {
        return state.layouts
      }
    },
    getActiveLayoutIndex: (state) => () => {
      return state.activeLayoutIndex
    },
    getCountdown: (state) => (idx?: number) => {
      if (typeof idx == "number") {
        return state.timers.countdowns[idx]
      } else {
        return state.timers.countdowns
      }
    },
    getTileConfig: (state) => (idx?: {row: number, col: number}) => {
      if (idx) {
        let tileConfig: TileConfig | undefined = undefined
        if (state.layouts[state.activeLayoutIndex]) {
          tileConfig = state.layouts[state.activeLayoutIndex].config[idx.row][idx.col];
        }
        if (tileConfig === undefined) tileConfig = {} as TileConfig
        if (tileConfig.name === undefined) tileConfig.name = "";
        if (tileConfig.config === undefined) tileConfig.config = {};
        return tileConfig;
      } else {
        // Migrate Tiles
        if (state.hasOwnProperty("tiles")) {
          if (!Array.isArray((state as any).tiles)) {
            (state as any).tiles = Object.values((state as any).tiles).slice(0, 4) as Array<TileConfig>
          }
          state.layouts = [
            ...DEFAULT_LAYOUTS,
            {
              name: "Migrated",
              config: [
                (state as any).tiles.slice(0, 2),
                (state as any).tiles.slice(2, 4)
              ]
            }
          ]
          state.activeLayoutIndex = state.layouts.length -1
          delete (state as any).tiles
        }
        return state.layouts[state.activeLayoutIndex];
      }
    },
    getConfig: (state) => () => {
      return state;
    },
    getNextEventPopup: (state) => () => {
      return typeof state.nextEventPopup != "undefined" ? state.nextEventPopup : true
    },
    getTourTaken: (state) => () => {
      return state.tourTaken
    }
  }
};

export default configStore;
